/**
 * FormPreview — live, read-only seller-form preview (PIM Wave P1 Task 10,
 * design notes Surface 5). Mounted in `BindingEditorPage`'s right-pane slot
 * (Task 8 left a mount comment there — see `pim-bindings-preview-slot`).
 *
 * ZERO-DRIFT RULE (binding, brief verbatim: "Never render the preview from
 * admin bindings data"): this component renders ONLY from
 * `GET /marketplace/categories/{id}/form-schema` (`useFormSchema`,
 * `hooks.ts`) — the exact same public, unauthenticated contract the seller
 * form (a later PIM wave) will consume. It never reads the admin
 * bindings/fields data already loaded elsewhere on `BindingEditorPage` (its
 * own props are just a bare `categoryId` — nothing else is threaded in), so
 * the preview is zero-drift-by-construction with what a seller will
 * actually see, not with whatever the admin happens to have cached.
 *
 * Conditional-visibility simulation (brief §5, mirroring the P0 backend's
 * one-level, fail-hidden law exactly): a local
 * `{ field_slug -> simulated value }` map, seeded LAZILY per read — there is
 * no "current product" here, only a bare category preview, so there is
 * nothing else a default could come from. A controller with NO entry in the
 * map is UNANSWERED, full stop (final-review Finding B corrects an earlier
 * "booleans default `false`" seeding, which let a `{eq, false}` rule
 * falsely reveal its dependent before the admin ever touched the widget —
 * the backend treats an unanswered controller as NOT MATCHED regardless of
 * the rule's operator/value, so the client must too; see `isFieldVisible`
 * and `defaultSimValue`). A rule-bearing field is visible iff:
 *   - its controller resolves to SOME field elsewhere in this schema's flat
 *     field list — controllers match by `field_slug` GLOBALLY across every
 *     group, not scoped to the dependent field's own section (P0 law), AND
 *   - that controller's `field_type` is `"select"` or `"boolean"` — the
 *     only two "simulatable" types; anything else (including a slug that
 *     doesn't resolve to any field at all) is FAIL-HIDDEN, mirroring the
 *     backend rather than inventing a laxer client default, AND
 *   - the controller has been ANSWERED (an entry exists in the sim map), AND
 *   - `operator === "eq"` ? the simulated value is strictly `===` the
 *     rule's `value` : `operator === "in"` ? the rule's `value` (must be an
 *     array) includes the simulated value : `false`.
 * Only `select`/`boolean` widgets are interactive (they toggle the
 * simulation); every other widget type is visually inert. Hidden fields
 * render nothing (`AnimatePresence` fades them out over 150ms,
 * `motion-reduce` safe via `useReducedMotion`) — a "N fields hidden by
 * current answers" line surfaces the count so an admin is never confused by
 * fields silently missing from the preview.
 *
 * States are hand-rolled rather than the shared `AdminStates` primitive
 * (deliberate, not an oversight): this surface needs (a) a skeleton that
 * mirrors a FORM's silhouette — grouped label/input pairs — not
 * `AdminStates`' generic list-row bars, and (b) THREE distinct error copies
 * keyed off `ApiError.code` (404 not-found / 409 archived / anything else
 * generic) rather than `AdminStates`' one static `errorLabel` string. The
 * chrome (device/language `Segmented`, refresh button) lives OUTSIDE the
 * state branch and stays mounted throughout — mirrors
 * `AdminMetricsDashboard.tsx`'s "plain reload lives outside AdminStates"
 * precedent — so the refresh button doubles as the error state's retry
 * action (brief: "other errors → generic + retry").
 */
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Upload } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { Segmented, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { useFormSchema } from "@/features/admin/category-bindings/hooks";
import type {
  FormFieldDto,
  FormFieldOptionDto,
  FormSchemaResponse,
  TrilingualText,
} from "@/features/admin/category-bindings/api";

type PreviewLang = "ru" | "ky" | "en";
type DeviceWidth = "mobile" | "desktop";

/** brief §3: "device-width Segmented (mobile ≈360px / desktop ≈640px)". */
const DEVICE_WIDTH_PX: Record<DeviceWidth, number> = { mobile: 360, desktop: 640 };

const INERT_CLASS =
  "min-h-[44px] w-full cursor-default rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] px-3 py-2 text-sm text-[var(--g-secondary)] opacity-80 focus:outline-none";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

function isBlank(text: string | null | undefined): boolean {
  return text === null || text === undefined || text.trim() === "";
}

/** Chosen-language text with RU fallback (brief §3/§4 — `TrilingualText.ru`
 * is always present; `ky`/`en` fall back whenever null/blank). */
function pickText(text: TrilingualText, lang: PreviewLang): string {
  if (lang === "ky" && !isBlank(text.ky)) return text.ky as string;
  if (lang === "en" && !isBlank(text.en)) return text.en as string;
  return text.ru;
}

/** Same fallback, but the WHOLE object may be `null` (hint/example — P0
 * law: null means "never authored", not just "missing a translation"). */
function pickOptionalText(text: TrilingualText | null, lang: PreviewLang): string | undefined {
  if (!text) return undefined;
  return pickText(text, lang);
}

/** Simulated-value type: booleans store `boolean`, selects store a
 * `value_slug` string (`""` = "no answer yet"). */
type SimValue = string | boolean;

/**
 * Widget-render-only default for a controller with NO entry in the sim map
 * yet (final-review Finding B). Selects render their "no answer" option as
 * `""` — a `value_slug` can never legitimately BE `""` (`_SLUG_PATTERN`
 * requires ≥1 char, `catalog_schema/schemas.py`), so `""` is a safe, inert
 * sentinel. Booleans get NO default at all (`undefined`, distinct from
 * `false`): an untouched boolean controller must render — and evaluate — as
 * UNANSWERED, never as "answered false". Seeding it `false` (the previous
 * behavior) let a `{eq, false}` rule reveal its dependent before the admin
 * ever touched the widget, contradicting the backend's fail-hidden law
 * (`service.py::_binding_is_visible` — no stored value row -> NOT VISIBLE,
 * unconditionally, regardless of the rule's operator/value). `isFieldVisible`
 * below never calls this — it treats a missing map entry as NOT MATCHED
 * directly, for both controller types. */
function defaultSimValue(fieldType: string): SimValue | undefined {
  return fieldType === "select" ? "" : undefined;
}

/** Flattens every group's fields (ungrouped bucket included) into one
 * ordered list — used both for the GLOBAL controller lookup (P0 law:
 * controllers resolve by `field_slug` across the WHOLE schema, not just
 * within one group) and for total/hidden field counts. Exported for
 * readability/testability even though TESTING IS DESCOPED for this task. */
export function flattenFields(schema: FormSchemaResponse): FormFieldDto[] {
  return schema.groups.flatMap((section) => section.fields);
}

/**
 * One-level, fail-hidden visibility mirror (brief §5). A field with no rule
 * is always visible. A rule-bearing field is visible only if its
 * controller resolves within `controllerTypeBySlug` to `"select"` or
 * `"boolean"` AND the CURRENT simulated value for that controller satisfies
 * the rule's operator. Any other outcome — unresolved slug, non-simulatable
 * controller type, malformed `in` value — is HIDDEN, never a thrown error
 * and never a fallback to "visible" (mirrors the backend's fail-hidden law
 * exactly rather than inventing a laxer client default).
 *
 * UNANSWERED controller (final-review Finding B): if `simValues` has NO
 * entry for the controller's slug at all, this returns NOT VISIBLE
 * immediately — before ever consulting the rule's operator/value. This
 * mirrors `service.py::_binding_is_visible`'s own first move once it
 * resolves a live controller field: `values_by_field_id.get(controller.id)`
 * returns `None` -> `return False`, unconditionally, for ANY rule (including
 * `{eq, false}`). A boolean controller has no default seeded into the map
 * (see `defaultSimValue`), so "untouched" and "answered false" are never
 * conflated here. A select controller's existing "no answer" convention
 * (an explicit `""` entry, or no entry at all) already falls out of this
 * same check — `""` can never equal a real rule `value` (a `value_slug` is
 * never empty) and can never appear in a rule's `value` array either, so
 * both `eq` and `in` already evaluated it as NOT MATCHED before this guard
 * existed; the guard just makes the "no entry at all" case explicit too.
 */
export function isFieldVisible(
  field: FormFieldDto,
  controllerTypeBySlug: ReadonlyMap<string, string>,
  simValues: Readonly<Record<string, SimValue>>,
): boolean {
  const rule = field.visibility_rule;
  if (!rule) return true;
  const controllerType = controllerTypeBySlug.get(rule.field_slug);
  if (controllerType !== "select" && controllerType !== "boolean") return false;
  if (!(rule.field_slug in simValues)) return false;
  const simValue = simValues[rule.field_slug];
  if (rule.operator === "eq") return simValue === rule.value;
  if (rule.operator === "in") return Array.isArray(rule.value) && rule.value.includes(simValue);
  return false;
}

/** `•` for required, `◦` for recommended, nothing for optional — legend row
 * below renders the same pair with text, so meaning is never color-only. */
function requirementMarker(requirement: string): ReactElement | null {
  if (requirement === "required") {
    return (
      <span aria-hidden="true" className="text-sm leading-none text-[var(--g-error)]">
        •
      </span>
    );
  }
  if (requirement === "recommended") {
    return (
      <span aria-hidden="true" className="text-sm leading-none text-[var(--g-warn)]">
        ◦
      </span>
    );
  }
  return null;
}

export interface FormPreviewProps {
  categoryId: string;
}

export function FormPreview({ categoryId }: FormPreviewProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const prefersReducedMotion = useReducedMotion() ?? false;

  const [device, setDevice] = useState<DeviceWidth>("mobile");
  const [lang, setLang] = useState<PreviewLang>("ru");
  const [simValues, setSimValues] = useState<Record<string, SimValue>>({});

  const query = useFormSchema(categoryId);
  const schema = query.data;

  // GLOBAL controller lookup (P0 law — see file header): every field's
  // slug -> field_type, flattened across every group in this schema.
  const controllerTypeBySlug = useMemo(() => {
    const map = new Map<string, string>();
    if (schema) {
      for (const field of flattenFields(schema)) {
        map.set(field.field_slug, field.field_type);
      }
    }
    return map;
  }, [schema]);

  const setSimValue = (slug: string, value: SimValue): void => {
    setSimValues((prev) => ({ ...prev, [slug]: value }));
  };

  /** Resets a controller back to UNANSWERED (final-review Finding B) — used
   * by the boolean widget's explicit "unanswered" segment so an admin can
   * return a rule-bearing dependent to hidden, not just true/false. Removes
   * the map entry entirely rather than writing a sentinel, so `rule.field_slug
   * in simValues` (the `isFieldVisible` fail-hidden guard) sees it exactly
   * like a controller that was never touched. */
  const clearSimValue = (slug: string): void => {
    setSimValues((prev) => {
      if (!(slug in prev)) return prev;
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  };

  const hiddenCount = useMemo(() => {
    if (!schema) return 0;
    let hidden = 0;
    for (const field of flattenFields(schema)) {
      if (field.visibility_rule && !isFieldVisible(field, controllerTypeBySlug, simValues)) {
        hidden += 1;
      }
    }
    return hidden;
  }, [schema, controllerTypeBySlug, simValues]);

  const totalFieldCount = schema ? flattenFields(schema).length : 0;

  const errorCode = query.error instanceof ApiError ? query.error.code : undefined;
  const isArchived = errorCode === "catalog_schema.category.archived";
  const isNotFound = errorCode === "catalog_schema.category.not_found";
  const errorMessage = isArchived
    ? t("pim.preview.archived")
    : isNotFound
      ? t("pim.preview.notFound")
      : t("pim.preview.error");

  /** `label` (already chosen-lang-resolved) is applied as `aria-label` on
   * each control's PRIMARY interactive element directly — not via a
   * wrapping element's `aria-labelledby`, which does not reliably cascade
   * an accessible name down to nested form controls. The size widget's
   * secondary unit `<select>` keeps its OWN distinct `aria-label`
   * ("Unit") rather than the field's label, so it announces as a unit
   * picker, not a duplicate of the field name. */
  function renderWidget(field: FormFieldDto, controlId: string, label: string): ReactNode {
    const example = pickOptionalText(field.example, lang);
    const simValue = simValues[field.field_slug] ?? defaultSimValue(field.field_type);

    switch (field.field_type) {
      case "boolean": {
        // Tri-state (final-review Finding B): an explicit "unanswered"
        // segment is a genuinely distinct state from true/false, not just a
        // rendering default — `Segmented` clamps an unmatched `value` to its
        // FIRST option's thumb position (`Math.max(0, findIndex(...))` in
        // `packages/ui/src/Segmented.tsx`), so a two-option true/false
        // control fed a sentinel would show a misleadingly "selected"-looking
        // thumb under one of them. A real third option sidesteps that
        // entirely and lets the admin explicitly return to "no answer"
        // (`clearSimValue`) after having set one, mirroring the backend's
        // "no stored value row" state rather than approximating it.
        const segValue = simValue === true ? "true" : simValue === false ? "false" : "unanswered";
        return (
          <div
            role="group"
            aria-label={label}
            data-testid={`pim-preview-widget-${field.field_slug}`}
          >
            <Segmented
              options={[
                { value: "unanswered", label: t("pim.preview.widgets.booleanUnanswered") },
                { value: "true", label: t("pim.preview.widgets.booleanTrue") },
                { value: "false", label: t("pim.preview.widgets.booleanFalse") },
              ]}
              value={segValue}
              onChange={(next) => {
                if (next === "true") setSimValue(field.field_slug, true);
                else if (next === "false") setSimValue(field.field_slug, false);
                else clearSimValue(field.field_slug);
              }}
            />
          </div>
        );
      }
      case "select": {
        const options = field.options ?? [];
        return (
          <select
            id={controlId}
            aria-label={label}
            data-testid={`pim-preview-widget-${field.field_slug}`}
            className={SELECT_CLASS}
            value={typeof simValue === "string" ? simValue : ""}
            onChange={(event) => setSimValue(field.field_slug, event.target.value)}
          >
            <option value="">{t("pim.preview.widgets.selectNone")}</option>
            {options.map((option: FormFieldOptionDto) => (
              <option key={option.value_slug} value={option.value_slug}>
                {pickText(option.label, lang)}
              </option>
            ))}
          </select>
        );
      }
      case "size":
        return (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-2">
              <input
                id={controlId}
                type="number"
                readOnly
                aria-disabled="true"
                aria-label={label}
                placeholder={example}
                data-testid={`pim-preview-widget-${field.field_slug}`}
                className={[INERT_CLASS, "flex-1"].join(" ")}
              />
              {/* Not the native `disabled` attribute (brief: "not focus-
                  trapped") — a fixed controlled `value` + no-op `onChange`
                  keeps this select focusable/keyboard-reachable while
                  preventing any real unit change; `aria-disabled` tells
                  assistive tech it is non-interactive. */}
              <select
                aria-disabled="true"
                aria-label={t("pim.preview.widgets.unitLabel")}
                value={field.canonical_unit ?? ""}
                onChange={() => undefined}
                className={[INERT_CLASS, "w-24 shrink-0"].join(" ")}
              >
                {(field.allowed_units ?? []).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
            {field.canonical_unit ? (
              <p className="text-xs text-[var(--g-secondary)]">
                {t("pim.preview.widgets.canonicalUnitNote", { unit: field.canonical_unit })}
              </p>
            ) : null}
          </div>
        );
      case "date":
        return (
          <input
            id={controlId}
            type="date"
            readOnly
            aria-disabled="true"
            aria-label={label}
            data-testid={`pim-preview-widget-${field.field_slug}`}
            className={INERT_CLASS}
          />
        );
      case "number":
        return (
          <input
            id={controlId}
            type="number"
            readOnly
            aria-disabled="true"
            aria-label={label}
            placeholder={example}
            data-testid={`pim-preview-widget-${field.field_slug}`}
            className={INERT_CLASS}
          />
        );
      case "image":
        return (
          <div
            id={controlId}
            aria-disabled="true"
            aria-label={label}
            data-testid={`pim-preview-widget-${field.field_slug}`}
            className="flex min-h-[88px] flex-col items-center justify-center gap-1.5 rounded-[var(--g-radius-sm)] border border-dashed border-[var(--g-hairline)] bg-[var(--g-elevated)] p-4 text-center text-xs text-[var(--g-secondary)]"
          >
            <Upload size={18} strokeWidth={1.75} aria-hidden />
            <span>{t("pim.preview.widgets.imagePlaceholder")}</span>
          </div>
        );
      case "text":
      default:
        // The form-schema DTO has no multiline/textarea flag (checked
        // `catalog_schema/schemas.py`'s `FormFieldDto` — no such field) —
        // every `text` field mirrors the DTO as a single-line input.
        return (
          <input
            id={controlId}
            type="text"
            readOnly
            aria-disabled="true"
            aria-label={label}
            placeholder={example}
            data-testid={`pim-preview-widget-${field.field_slug}`}
            className={INERT_CLASS}
          />
        );
    }
  }

  function renderFieldRow(field: FormFieldDto): ReactElement {
    const controlId = `pim-preview-field-${field.field_slug}`;
    const label = pickText(field.label, lang);
    const hint = pickOptionalText(field.hint, lang);
    const requirementSrText =
      field.requirement === "required" || field.requirement === "recommended"
        ? t(`pim.preview.legend.${field.requirement}`)
        : null;

    return (
      <div className="flex flex-col gap-1.5" data-testid="pim-preview-field-row">
        <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--g-primary-ink)]">
          {requirementMarker(field.requirement)}
          {label}
          {requirementSrText ? <span className="sr-only">{`(${requirementSrText})`}</span> : null}
        </span>
        {renderWidget(field, controlId, label)}
        {hint ? <p className="text-xs text-[var(--g-secondary)]">{hint}</p> : null}
      </div>
    );
  }

  return (
    <section
      aria-label={t("pim.preview.regionLabel")}
      data-testid="pim-form-preview"
      className="flex flex-col gap-3 rounded-[var(--g-radius-md)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[var(--g-primary-ink)]">{t("pim.preview.title")}</h2>
        <button
          type="button"
          data-testid="pim-preview-refresh"
          aria-label={t("pim.preview.refresh")}
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            size={16}
            strokeWidth={1.75}
            aria-hidden
            className={query.isFetching ? "g-spin" : undefined}
          />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label={t("pim.preview.deviceLabel")} data-testid="pim-preview-device">
          <Segmented
            options={[
              { value: "mobile", label: t("pim.preview.device.mobile") },
              { value: "desktop", label: t("pim.preview.device.desktop") },
            ]}
            value={device}
            onChange={(next) => {
              if (next === "mobile" || next === "desktop") setDevice(next);
            }}
          />
        </div>
        <div role="group" aria-label={t("pim.preview.languageLabel")} data-testid="pim-preview-language">
          <Segmented
            options={[
              { value: "ru", label: "RU" },
              { value: "ky", label: "KG" },
              { value: "en", label: "EN" },
            ]}
            value={lang}
            onChange={(next) => {
              if (next === "ru" || next === "ky" || next === "en") setLang(next);
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--g-secondary)]">
        <span className="flex items-center gap-1">
          <span aria-hidden="true" className="text-[var(--g-error)]">
            •
          </span>
          {t("pim.preview.legend.required")}
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden="true" className="text-[var(--g-warn)]">
            ◦
          </span>
          {t("pim.preview.legend.recommended")}
        </span>
      </div>

      <div className="mx-auto w-full" style={{ maxWidth: DEVICE_WIDTH_PX[device] }}>
        {query.isLoading ? (
          <div
            role="status"
            aria-busy="true"
            aria-label={t("pim.preview.loading")}
            data-testid="pim-preview-loading"
            className="flex flex-col gap-5"
          >
            {[0, 1].map((groupIndex) => (
              <div key={groupIndex} className="flex flex-col gap-3">
                <Skeleton w="45%" h={14} />
                {[0, 1, 2].map((fieldIndex) => (
                  <div key={fieldIndex} className="flex flex-col gap-1.5">
                    <Skeleton w="35%" h={10} />
                    <Skeleton w="100%" h={40} r={10} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            data-testid="pim-preview-error"
            className="flex items-start gap-2 rounded-[var(--g-radius-sm)] bg-[var(--g-error)]/10 p-3 text-sm text-[var(--g-error)]"
          >
            <AlertTriangle size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        ) : !schema ? null : totalFieldCount === 0 ? (
          <p data-testid="pim-preview-empty" className="py-6 text-center text-sm text-[var(--g-secondary)]">
            {t("pim.preview.empty")}
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            {hiddenCount > 0 ? (
              <p
                role="status"
                aria-live="polite"
                data-testid="pim-preview-hidden-count"
                className="text-xs text-[var(--g-secondary)]"
              >
                {t("pim.preview.hiddenCount", { count: hiddenCount })}
              </p>
            ) : null}
            {schema.groups.map((section) => (
              <div key={section.group?.id ?? "ungrouped"} className="flex flex-col gap-3">
                {section.group ? (
                  <h3 className="text-sm font-semibold text-[var(--g-primary-ink)]">
                    {pickText(section.group.name, lang)}
                  </h3>
                ) : null}
                <AnimatePresence initial={false}>
                  {section.fields
                    .filter((field) => isFieldVisible(field, controllerTypeBySlug, simValues))
                    .map((field) => (
                      <motion.div
                        key={field.field_slug}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
                      >
                        {renderFieldRow(field)}
                      </motion.div>
                    ))}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default FormPreview;
